import type { ViolationData, ViolationFixability } from '../types/violation';

export const UNFIXABLE_BOX_STROKE = '#7c3aed';
export const UNFIXABLE_BOX_FILL = 'rgba(124, 58, 237, 0.22)';
export const UNFIXABLE_BOX_FILL_SELECTED = 'rgba(124, 58, 237, 0.35)';

const SHOW_UNFIXABLE_STORAGE_PREFIX = 'violationCanvas.showUnfixable';

/** Turn literal "\n"/"\r\n" escape sequences (e.g. from source data that was never unescaped) into real line breaks. */
export function normalizeMultilineText(text: string): string {
    return text.replace(/\\r\\n|\\n|\\r/g, '\n');
}

function normalizeOptionalText(text: string | null | undefined): string | undefined {
    return text == null ? undefined : normalizeMultilineText(text);
}

export function isUnfixableViolation(v: Pick<ViolationData, 'fixability'>): boolean {
    return v.fixability === 'unfixable';
}

export function fixabilityLabel(fixability?: ViolationFixability): string {
    if (fixability === 'unfixable') return '修正不可';
    if (fixability === 'fixable') return '修正可能';
    return '未判定';
}

export function fixabilitySourceLabel(source?: string): string | null {
    if (source === 'ai') return 'AI分類';
    if (source === 'manual') return '手動変更';
    return null;
}

export function showUnfixableStorageKey(jobId?: string, urlId?: string): string | null {
    if (!jobId || !urlId) return null;
    return `${SHOW_UNFIXABLE_STORAGE_PREFIX}.${jobId}.${urlId}`;
}

export function readShowUnfixablePreference(jobId?: string, urlId?: string): boolean {
    const key = showUnfixableStorageKey(jobId, urlId);
    if (!key || typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(key);
    if (raw === 'false') return false;
    if (raw === 'true') return true;
    return true;
}

export function writeShowUnfixablePreference(jobId: string | undefined, urlId: string | undefined, value: boolean): void {
    const key = showUnfixableStorageKey(jobId, urlId);
    if (!key || typeof window === 'undefined') return;
    window.localStorage.setItem(key, value ? 'true' : 'false');
}

/** Longest substring of media rule text that appears in haystack (indices in description). */
export function findLongestMediaRuleMatch(
    mediaRuleDescription: string,
    haystack: string
): { start: number; end: number } | null {
    const desc = mediaRuleDescription.trim();
    const text = haystack.trim();
    if (!desc || !text) return null;

    let bestLen = 0;
    let bestStart = -1;
    let bestEnd = -1;

    for (let i = 0; i < desc.length; i++) {
        for (let j = i + 1; j <= desc.length; j++) {
            const sub = desc.slice(i, j);
            if (sub.length < 2) continue;
            if (text.includes(sub) && sub.length > bestLen) {
                bestLen = sub.length;
                bestStart = i;
                bestEnd = j;
            }
        }
    }

    if (bestStart < 0) return null;
    return { start: bestStart, end: bestEnd };
}

/** Excerpt from guideline media rules that best matches this violation's edit text. */
/** First exact occurrence of part within full guideline media rule text. */
export function findPartRangeInMediaRule(
    mediaRuleDescription: string | undefined,
    part: string | undefined
): { start: number; end: number } | null {
    const full = mediaRuleDescription?.trim() ?? '';
    const excerpt = part?.trim() ?? '';
    if (!full || !excerpt) return null;
    const idx = full.indexOf(excerpt);
    if (idx < 0) return null;
    return { start: idx, end: idx + excerpt.length };
}

export function findMatchingMediaRuleExcerpt(
    mediaRuleDescription: string | undefined,
    violationEditNote?: string,
    violationContent?: string
): string | null {
    const description = mediaRuleDescription?.trim() ?? '';
    if (!description) return null;

    const violationText = [violationEditNote, violationContent]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join('\n');
    if (!violationText) return null;

    const match = findLongestMediaRuleMatch(description, violationText);
    if (!match) return null;
    return description.slice(match.start, match.end);
}

export function violationTextForRuleMatch(violation: {
    editNote?: string;
    violationContent?: string;
}): string {
    return [violation.editNote, violation.violationContent]
        .map((s) => s?.trim())
        .filter(Boolean)
        .join('\n');
}

export function countFixabilityBreakdown(
    violations: ViolationData[]
): { total: number; fixable: number; unfixable: number } {
    const active = violations.filter((v) => !v.deleted);
    const unfixable = active.filter(isUnfixableViolation).length;
    return {
        total: active.length,
        fixable: active.length - unfixable,
        unfixable,
    };
}

export function mapViolationFromApi(violation: Record<string, unknown>): Partial<ViolationData> {
    return {
        fixability: (violation.fixability as ViolationFixability) ?? 'fixable',
        unfixableDomain: (violation.unfixable_domain as string) ?? undefined,
        unfixableRuleDescription: normalizeOptionalText(violation.unfixable_rule_description as string | undefined),
        unfixableReasonDescriptionPart: normalizeOptionalText(
            (violation.unfixable_reason_description_part as string | undefined)
            ?? (violation.reason as string | undefined)
        ),
        fixabilityClassifiedAt: (violation.fixability_classified_at as string) ?? undefined,
        fixabilitySource: (violation.fixability_source as ViolationData['fixabilitySource']) ?? undefined,
        fixabilityOverriddenBy: (violation.fixability_overridden_by as string) ?? undefined,
        fixabilityOverriddenAt: (violation.fixability_overridden_at as string) ?? undefined,
    };
}
